import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  IconButton,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import StableCKEditor from './StableCKEditor';
import ClassicEditor from '../utils/ckeditor';
import { toEditorHtml } from '../utils/sanitizeHtml';
import { tasksAPI, taskStatusesAPI, usersAPI, sprintsAPI, attachmentsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { TASK_TYPES, taskTypeIcon } from '../utils/taskTypes';

function TaskCreateDialog({ open, onClose, projectId, onTaskCreated, projects, customers, allProjects }) {
  const { isClient } = useAuth();
  const clientUser = isClient && isClient();
  const [formData, setFormData] = useState({
    customer_id: '',
    project_id: projectId || '',
    task_id: '',
    name: '',
    description: '',
    status_id: '',
    task_type: 'task',
    category: '',
    priority: 'medium',
    assignee_id: '',
    due_date: '',
    effort_estimate: '',
    sprint_id: '',
    is_timetracker_only: false,
  });

  const [taskStatuses, setTaskStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);   // vorgemerkte Anhänge (vor dem Anlegen)
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setFiles([]);
      loadUsers();
      tasksAPI.getCategories().then(r => setCategories(r.data || [])).catch(() => {});
      if (projectId) {
        setFormData(prev => ({ ...prev, project_id: projectId }));
        loadProjectData(projectId);
      } else {
        // Load global statuses when no project is selected
        loadProjectData(null);
      }
      // Initialize filtered projects
      if (allProjects && allProjects.length > 0) {
        setFilteredProjects(allProjects);
      } else if (projects && projects.length > 0) {
        setFilteredProjects(projects);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId, projects, allProjects]);

  // Filter projects when customer changes
  useEffect(() => {
    if (formData.customer_id && allProjects) {
      const filtered = allProjects.filter(p => p.customer_id === parseInt(formData.customer_id));
      setFilteredProjects(filtered);
      // Reset project if current project doesn't belong to selected customer
      const currentProject = allProjects.find(p => p.id === parseInt(formData.project_id));
      if (!currentProject || currentProject.customer_id !== parseInt(formData.customer_id)) {
        setFormData(prev => ({ ...prev, project_id: '' }));
      }
    } else if (allProjects) {
      setFilteredProjects(allProjects);
    } else if (projects) {
      setFilteredProjects(projects);
    }
  }, [formData.customer_id, formData.project_id, allProjects, projects]);

  const loadUsers = async () => {
    try {
      const usersRes = await usersAPI.getAll();
      setUsers(usersRes.data || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const loadProjectData = async (projectId) => {
    try {
      // Load statuses - project-specific or global
      const statusesRes = await taskStatusesAPI.getAll(projectId);
      const statuses = statusesRes.data || [];
      setTaskStatuses(statuses);

      // Load sprints only if project is selected
      if (projectId) {
        const sprintsRes = await sprintsAPI.getAll(projectId);
        setSprints(sprintsRes.data || []);
      } else {
        setSprints([]);
      }

      // Set default status to "Offen" if available (first status with order 0)
      if (statuses.length > 0 && !formData.status_id) {
        const defaultStatus = statuses.find(s => s.order === 0) || statuses[0];
        setFormData(prev => ({
          ...prev,
          status_id: defaultStatus.id
        }));
      }
    } catch (err) {
      console.error('Failed to load project data:', err);
      setError('Fehler beim Laden der Projektdaten');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Load project-specific data when project changes (or global statuses if cleared)
    if (name === 'project_id') {
      loadProjectData(value || null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation: Either project_id OR customer_id must be provided
    if (!formData.project_id && !formData.customer_id) {
      setError('Bitte wählen Sie einen Kunden oder ein Projekt aus');
      setLoading(false);
      return;
    }

    try {
      const createData = { ...formData };
      if (!createData.assignee_id) createData.assignee_id = null;
      if (!createData.due_date) createData.due_date = null;
      if (!createData.effort_estimate) createData.effort_estimate = null;
      if (!createData.sprint_id) createData.sprint_id = null;
      if (!createData.status_id) createData.status_id = null;
      if (!createData.project_id) createData.project_id = null;
      if (!createData.task_id) createData.task_id = null;
      if (!createData.category) createData.category = null;

      const resp = await tasksAPI.create(createData);
      const newId = resp.data && resp.data.id;

      // Vorgemerkte Anhänge an den neu erstellten Task hochladen
      if (newId && files.length) {
        for (const f of files) {
          try {
            await attachmentsAPI.upload(newId, f);
          } catch (uerr) {
            console.error('Upload fehlgeschlagen:', f.name, uerr);
          }
        }
      }

      if (onTaskCreated) {
        onTaskCreated();
      }

      onClose();
    } catch (err) {
      console.error('Failed to create task:', err);
      setError(err.response?.data?.error || 'Fehler beim Erstellen des Tasks');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Task erstellen
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ px: 4, py: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Taskname und Task-ID - Erste Zeile */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                sx={{ flex: '1 1 70%', minWidth: '300px' }}
                label="Taskname"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                autoFocus
              />
              <TextField
                sx={{ flex: '1 1 25%', minWidth: '150px' }}
                label="Task-ID (optional)"
                name="task_id"
                value={formData.task_id}
                onChange={handleChange}
                placeholder="z.B. PROJ-123"
              />
            </Box>

            {/* Kunde-Auswahl - nur wenn kein projectId prop übergeben wurde */}
            {!projectId && customers && customers.length > 0 && (
              <FormControl fullWidth required>
                <InputLabel>Kunde</InputLabel>
                <Select
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleChange}
                  label="Kunde"
                >
                  <MenuItem value="">
                    <em>Kunde auswählen</em>
                  </MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.company_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Projekt-Auswahl - optional wenn Kunde ausgewählt */}
            {!projectId && (
              <FormControl fullWidth disabled={!formData.customer_id && customers && customers.length > 0}>
                <InputLabel>Projekt (optional)</InputLabel>
                <Select
                  name="project_id"
                  value={formData.project_id}
                  onChange={handleChange}
                  label="Projekt (optional)"
                >
                  <MenuItem value="">
                    <em>Kein Projekt (nur Zeiterfassung)</em>
                  </MenuItem>
                  {filteredProjects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Task-Typ Toggle - nur für interne User */}
            {!clientUser && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_timetracker_only}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_timetracker_only: e.target.checked }))}
                    name="is_timetracker_only"
                  />
                }
                label="Nur für Zeiterfassung (erscheint nicht im Board)"
              />
            )}

            {/* Typ-Zeile */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                <InputLabel>Typ</InputLabel>
                <Select
                  name="task_type"
                  value={formData.task_type}
                  onChange={handleChange}
                  label="Typ"
                >
                  {TASK_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {taskTypeIcon(t.value, { fontSize: 'small', sx: { color: t.color } })}
                        {t.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ flex: '1 1 60%', minWidth: '240px' }}>
                <InputLabel>Kategorie</InputLabel>
                <Select name="category" value={formData.category} onChange={handleChange} label="Kategorie">
                  <MenuItem value=""><em>Keine</em></MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.key} value={c.key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: c.color }} />
                        {c.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Dritte Zeile - Status, Priorität, Zugewiesen */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status_id"
                  value={formData.status_id}
                  onChange={handleChange}
                  label="Status"
                >
                  <MenuItem value="">
                    <em>Kein Status</em>
                  </MenuItem>
                  {taskStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                <InputLabel>Priorität</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  label="Priorität"
                >
                  <MenuItem value="low">Niedrig</MenuItem>
                  <MenuItem value="medium">Mittel</MenuItem>
                  <MenuItem value="high">Hoch</MenuItem>
                </Select>
              </FormControl>

              {!clientUser && (
                <FormControl sx={{ flex: '1 1 30%', minWidth: '200px' }}>
                  <InputLabel>Zugewiesen an</InputLabel>
                  <Select
                    name="assignee_id"
                    value={formData.assignee_id}
                    onChange={handleChange}
                    label="Zugewiesen an"
                  >
                    <MenuItem value="">
                      <em>Nicht zugewiesen</em>
                    </MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            {clientUser && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Die Aufgabe wird automatisch dem Projektmanager zugewiesen, der sie weiter delegiert.
              </Typography>
            )}

            {/* Vierte Zeile - Datum, Aufwand, Sprint */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                sx={{ flex: '1 1 30%', minWidth: '200px' }}
                label="Fälligkeitsdatum"
                name="due_date"
                type="date"
                value={formData.due_date}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                sx={{ flex: '1 1 30%', minWidth: '200px' }}
                label="Aufwand (Punkte)"
                name="effort_estimate"
                type="number"
                value={formData.effort_estimate}
                onChange={handleChange}
                inputProps={{ min: 0 }}
              />

              <FormControl sx={{ flex: '1 1 30%', minWidth: '200px' }} disabled={!formData.project_id}>
                <InputLabel>Sprint</InputLabel>
                <Select
                  name="sprint_id"
                  value={formData.sprint_id}
                  onChange={handleChange}
                  label="Sprint"
                >
                  <MenuItem value="">
                    <em>Kein Sprint</em>
                  </MenuItem>
                  {sprints.map((sprint) => (
                    <MenuItem key={sprint.id} value={sprint.id}>
                      {sprint.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Beschreibung - Letzte Zeile, volle Breite */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                Beschreibung
              </Typography>
              <Box
                sx={{
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: 1,
                  '&:hover': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                  },
                  '& .ck-editor__editable': {
                    minHeight: '300px',
                  },
                }}
              >
                <StableCKEditor
                  editor={ClassicEditor}
                  data={toEditorHtml(formData.description || '')}
                  config={{
                    placeholder: 'Detaillierte Beschreibung der Aufgabe...',
                    toolbar: [
                      'heading', '|',
                      'bold', 'italic', 'link', '|',
                      'bulletedList', 'numberedList', '|',
                      'blockQuote', 'insertTable', '|',
                      'undo', 'redo'
                    ],
                  }}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setFormData(prev => ({
                      ...prev,
                      description: data,
                    }));
                  }}
                />
              </Box>
            </Box>

            {/* Anhänge (z. B. Screenshot zu einem Bug) */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                Anhänge
              </Typography>
              <Box
                onClick={() => fileRef.current && fileRef.current.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (e.dataTransfer.files.length) setFiles((prev) => [...prev, ...Array.from(e.dataTransfer.files)]);
                }}
                sx={{
                  border: '2px dashed', borderColor: 'divider', borderRadius: 2, p: 2,
                  textAlign: 'center', cursor: 'pointer',
                }}
              >
                <input
                  ref={fileRef} type="file" multiple hidden
                  onChange={(e) => {
                    if (e.target.files.length) setFiles((prev) => [...prev, ...Array.from(e.target.files)]);
                    e.target.value = '';
                  }}
                />
                <CloudUploadIcon sx={{ color: 'text.secondary', mr: 1, verticalAlign: 'middle' }} />
                <Typography variant="body2" component="span" color="text.secondary">
                  Screenshot/Datei hierher ziehen oder klicken · max. 10 MB
                </Typography>
              </Box>
              {files.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {files.map((f, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.25 }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        {f.name} <Typography component="span" variant="caption" color="text.secondary">
                          ({Math.round(f.size / 1024)} KB)</Typography>
                      </Typography>
                      <IconButton size="small" onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {error && (
            <Box mt={2} color="error.main">
              {error}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Erstellen...' : 'Task erstellen'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default TaskCreateDialog;
